export const CACHE_POLICIES = {
  CACHE_FIRST: 'cache-first',
  NETWORK_FIRST: 'network-first',
  STALE_WHILE_REVALIDATE: 'stale-while-revalidate',
}

const DB_NAME = 'gapco-query-cache'
const DB_VERSION = 1
const STORE_NAME = 'entries'
const SESSION_KEY = 'gapcoQueryCacheSessionId'
const DEFAULT_MAX_AGE_MS = 5 * 60 * 1000
const CACHE_CONTROL_MAX_AGE_RE = /(?:^|,\s*)max-age=(\d+)/i

const memory = new Map()
const inflight = new Map()
let nextNavigationIsHistoryRestore = false
let currentNavigationIsHistoryRestore = isInitialHistoryRestore()

let dbPromise = null
let identityProvider = () => 'anonymous'

export function setIdentityProvider(fn) {
  identityProvider = typeof fn === 'function' ? fn : identityProvider
}

function now() {
  return Date.now()
}

function getSessionId() {
  if (typeof window === 'undefined' || !window.sessionStorage) return 'server'
  let id = window.sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = (
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`
    )
    window.sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

function getUserIdentity() {
  try {
    return identityProvider() || 'anonymous'
  } catch {
    return 'anonymous'
  }
}

export function wasFullReload() {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return false
  const nav = performance.getEntriesByType('navigation')?.[0]
  return nav?.type === 'reload'
}

function isInitialHistoryRestore() {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return false
  const nav = performance.getEntriesByType('navigation')?.[0]
  return nav?.type === 'back_forward'
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    nextNavigationIsHistoryRestore = true
  })
  if (window.navigation) {
    window.navigation.addEventListener('navigate', (event) => {
      if (event.navigationType === 'traverse') {
        nextNavigationIsHistoryRestore = true
      }
    })
  }
}

export function getNavigationPolicy() {
  return currentNavigationIsHistoryRestore
    ? CACHE_POLICIES.CACHE_FIRST
    : CACHE_POLICIES.NETWORK_FIRST
}

export function markSpaNavigation({ historyRestore = null } = {}) {
  currentNavigationIsHistoryRestore = historyRestore === null
    ? nextNavigationIsHistoryRestore
    : !!historyRestore
  nextNavigationIsHistoryRestore = false
}

function normalizePath(rawUrl, baseURL = '') {
  const base = baseURL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
  const url = new URL(rawUrl, base)
  return url.pathname.replace(/\/+$/, '/') || '/'
}

function normalizeParams(params = {}) {
  const pairs = []
  Object.keys(params || {}).sort().forEach(key => {
    const value = params[key]
    if (value === null || typeof value === 'undefined' || value === '') return
    const values = Array.isArray(value) ? value : [value]
    values
      .map(item => String(item))
      .sort()
      .forEach(item => pairs.push([key, item]))
  })
  return pairs
}

export function buildCacheKey(client, rawUrl, config = {}) {
  const path = normalizePath(rawUrl, client?.defaults?.baseURL)
  const params = normalizeParams(config.params)
  return JSON.stringify({
    method: 'GET',
    path,
    params,
    user: getUserIdentity(),
    session: getSessionId(),
  })
}

function getHeaders(response) {
  return response?.headers || {}
}

function getHeader(headers, name) {
  if (!headers) return null
  const lower = name.toLowerCase()
  return headers[name] || headers[lower] || null
}

function expiresAtFromHeaders(headers) {
  const cacheControl = getHeader(headers, 'cache-control')
  const match = cacheControl?.match(CACHE_CONTROL_MAX_AGE_RE)
  if (match) return now() + Number(match[1]) * 1000
  return now() + DEFAULT_MAX_AGE_MS
}

function responseToEntry(key, response, prior = null) {
  const headers = getHeaders(response)
  const timestamp = now()
  return {
    key,
    payload: response.data,
    status: response.status,
    headers: {
      etag: getHeader(headers, 'etag') || prior?.headers?.etag || null,
      lastModified: getHeader(headers, 'last-modified') || prior?.headers?.lastModified || null,
      cacheControl: getHeader(headers, 'cache-control') || prior?.headers?.cacheControl || null,
    },
    createdAt: prior?.createdAt || timestamp,
    validatedAt: timestamp,
    expiresAt: expiresAtFromHeaders(headers),
  }
}

function entryToResponse(entry, fromCache = true) {
  return {
    data: entry.payload,
    status: entry.status || 200,
    headers: {
      etag: entry.headers?.etag,
      'last-modified': entry.headers?.lastModified,
      'cache-control': entry.headers?.cacheControl,
    },
    fromCache,
    cacheEntry: entry,
  }
}

function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
  return dbPromise
}

async function getStoredEntry(key) {
  if (memory.has(key)) return { entry: memory.get(key), source: 'memory' }
  const db = await openDb()
  if (!db) return { entry: null, source: 'none' }
  return new Promise((resolve) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key)
    req.onsuccess = () => {
      const entry = req.result || null
      if (entry) memory.set(key, entry)
      resolve({ entry, source: entry ? 'indexeddb' : 'miss' })
    }
    req.onerror = () => resolve({ entry: null, source: 'error' })
  })
}

async function putStoredEntry(entry) {
  memory.set(entry.key, entry)
  const db = await openDb()
  if (!db) return
  return new Promise((resolve) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(entry)
    req.onsuccess = () => resolve()
    req.onerror = () => resolve()
  })
}

async function deleteStoredEntry(key) {
  memory.delete(key)
  const db = await openDb()
  if (!db) return
  return new Promise((resolve) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(key)
    req.onsuccess = () => resolve()
    req.onerror = () => resolve()
  })
}

async function getAllEntries() {
  const db = await openDb()
  if (!db) return Array.from(memory.values())
  return new Promise((resolve) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
    req.onsuccess = () => resolve(req.result || [])
    req.onerror = () => resolve(Array.from(memory.values()))
  })
}

function conditionalConfig(config, entry) {
  const headers = { ...(config.headers || {}) }
  if (entry?.headers?.etag) headers['If-None-Match'] = entry.headers.etag
  if (entry?.headers?.lastModified) headers['If-Modified-Since'] = entry.headers.lastModified
  return {
    ...config,
    headers,
    validateStatus(status) {
      return (status >= 200 && status < 300) || status === 304
    },
  }
}

async function revalidate(client, rawUrl, config, key, entry) {
  const requestKey = `${key}:request`
  if (inflight.has(requestKey)) return inflight.get(requestKey)
  const promise = client.get(rawUrl, conditionalConfig(config, entry))
    .then(async response => {
      if (response.status === 304 && entry) {
        const updated = {
          ...entry,
          validatedAt: now(),
          expiresAt: expiresAtFromHeaders(response.headers || entry.headers),
        }
        await putStoredEntry(updated)
        return entryToResponse(updated, true)
      }
      const nextEntry = responseToEntry(key, response, entry)
      await putStoredEntry(nextEntry)
      return { ...response, fromCache: false, cacheEntry: nextEntry }
    })
    .finally(() => {
      inflight.delete(requestKey)
    })
  inflight.set(requestKey, promise)
  return promise
}

export async function cachedGet(client, rawUrl, config = {}, options = {}) {
  const policy = options.policy || getNavigationPolicy()
  const key = buildCacheKey(client, rawUrl, config)
  const { entry } = await getStoredEntry(key)

  if (entry && (policy === CACHE_POLICIES.CACHE_FIRST || policy === CACHE_POLICIES.STALE_WHILE_REVALIDATE)) {
    if (policy === CACHE_POLICIES.STALE_WHILE_REVALIDATE) {
      revalidate(client, rawUrl, config, key, entry).catch(() => {})
    }
    const cachedResponse = entryToResponse(entry, true)
    options.onCached?.(cachedResponse)
    return cachedResponse
  }

  const response = await revalidate(client, rawUrl, config, key, entry)
  if (response.fromCache) options.onCached?.(response)
  return response
}

function parseKey(key) {
  try {
    return JSON.parse(key)
  } catch {
    return null
  }
}

export async function cleanup() {
  const entries = await getAllEntries()
  const session = getSessionId()
  const timestamp = now()
  const keys = entries
    .filter(entry => {
      const keyData = parseKey(entry.key)
      return !keyData || keyData.session !== session || (entry.expiresAt && entry.expiresAt < timestamp)
    })
    .map(entry => entry.key)
  await Promise.all(keys.map(deleteStoredEntry))
  return keys.length
}

export async function clearAll() {
  memory.clear()
  inflight.clear()
  const db = await openDb()
  if (!db) return
  return new Promise((resolve) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).clear()
    req.onsuccess = () => resolve()
    req.onerror = () => resolve()
  })
}
